import { MODEL, PRIME } from '../../core/estimate.js';
import params from '../../data/sba-params.json';
import { PageHead } from '../components.js';

const pct = (x: number, d = 2) => `${(x * 100).toFixed(d)}%`;

export function ModelCard() {
  const m = MODEL.meta;
  const bt = m.backtest;
  return (
    <>
      <PageHead eyebrow="Model card" title="How the estimate works">
        What it uses, what it never uses, how it was tested, and where it falls short.
      </PageHead>
      <div className="stack-lg">
        <section className="card">
          <h2>The question it answers</h2>
          <p>
            &ldquo;Of SBA 7(a) loans like this one that lenders approved recently, how many were charged off within{' '}
            {m.outcome.horizonMonths / 12} years?&rdquo; The level is set from loans approved in FY
            {m.outcome.calibratedOnFiscalYears.join(' and FY')}, the newest with three years of history.
          </p>
          <p className="callout warn">
            SBA publishes only approved loans, so the model cannot say whether a loan would be approved. It describes how
            comparable loans performed after approval.
          </p>
        </section>

        <div className="grid-2">
          <section className="card">
            <h2>What it uses</h2>
            <ul>
              <li>Industry sector (2-digit NAICS)</li>
              <li>Loan amount and repayment length, and how they combine</li>
              <li>SBA&rsquo;s business-age category (startup, 2 years or less, more than 2 years, change of ownership)</li>
              <li>Jobs supported per dollar — self-reported to SBA and not verified by SBA</li>
            </ul>
            <p className="small muted">Use of funds is collected for your plan but carries no weight: SBA&rsquo;s public data has no such field.</p>
          </section>
          <section className="card">
            <h2>What it never uses</h2>
            <ul>
              <li>{params.excludedInputs.protectedCharacteristics.join(', ')}</li>
              <li>Zip code, address or neighbourhood — location is a well-documented stand-in for race</li>
              <li>Name, employer or school</li>
            </ul>
            <p className="small muted">The trade-off: without those fields, fairness across those groups cannot be measured here.</p>
          </section>
        </div>

        <section className="card">
          <h2>How it was built</h2>
          <p>
            All {m.loans.toLocaleString('en-US')} weighted SBA 7(a) term loans in the fit come from SBA&rsquo;s public FOIA
            data (as of {m.asOf}), compacted into counts so nothing was sampled away. The model is a regularised logistic
            regression. {m.fitWindow.description}
          </p>
          <p className="small muted">
            Rates are what comparable recent variable-rate SBA loans were actually charged, converted to today&rsquo;s prime
            ({PRIME.pct}% as of {PRIME.asOf}, Federal Reserve H.15) and capped at SBA&rsquo;s published maximum.
          </p>
        </section>

        <section className="card">
          <h2>How it was tested</h2>
          <p className="muted">Built on older loans, then scored on loans it never saw, using the exact code that runs this page.</p>
          <div className="table-scroll">
            <table className="table">
              <tbody>
                <tr><td>Ranks unseen FY{bt.testFiscalYears.join('–')} loans ({bt.testLoans.toLocaleString('en-US')} loans)</td><td className="r">picks the failed loan as riskier {pct(bt.auc, 0)} of the time</td></tr>
                <tr><td>Industry alone, for comparison</td><td className="r">{pct(bt.industryOnlyAuc, 0)} (a coin flip is 50%)</td></tr>
                <tr><td>Ranks long-run outcomes (FY{bt.proxyCheck.fiscalYears[0]}–{bt.proxyCheck.fiscalYears.at(-1)} loans, held out)</td><td className="r">{pct(bt.proxyCheck.auc, 0)}</td></tr>
                <tr><td>Rate ranges containing new FY{bt.rates.testFiscalYears.join('–')} loans</td><td className="r">{pct(bt.rates.coverageP10toP90, 0)} (target 80%)</td></tr>
                <tr><td>One year ahead: predicted vs actual for FY{bt.forward.testFY}</td><td className="r">{pct(bt.forward.predictedRate)} vs {pct(bt.forward.actualRate)}</td></tr>
              </tbody>
            </table>
          </div>
          <h3 style={{ marginTop: 18 }}>Unseen loans by band</h3>
          <div className="table-scroll">
            <table className="table">
              <thead><tr><th>Band</th><th className="r">Loans</th><th className="r">Actually charged off within 3 years</th></tr></thead>
              <tbody>{bt.byBand.map((b) => <tr key={b.band}><td>{b.band}</td><td className="r">{b.loans.toLocaleString('en-US')}</td><td className="r">{pct(b.actualRate)}</td></tr>)}</tbody>
            </table>
          </div>
        </section>

        <section className="card">
          <h2>Known limits</h2>
          <ul>
            <li>Five public facts carry limited signal. Lenders decide mainly on credit, cash flow and collateral, which this model never sees.</li>
            <li>It finds patterns, not causes. Choosing a different term or waiting a year will not by itself change your business&rsquo;s risk.</li>
            <li>Charge-offs have been rising. Calibrated on the newest loans, the figure can run low in a rising market — it ran about {pct(1 - bt.forward.predictedRate / bt.forward.actualRate, 0)} low a year ahead in testing.</li>
            <li>It ranks early trouble better than whole-life trouble. Most SBA charge-offs happen around year five.</li>
            <li>Loans approved in FY2020–2021 are left out: pandemic payment relief hid failures that would otherwise have happened.</li>
            <li>Fixed-rate loans are not modelled; current fixed-rate limits could not be confirmed from an SBA source.</li>
          </ul>
        </section>

        <section className="card">
          <h2>Outcome parity across industry groups</h2>
          <p className="small muted">
            How each industry&rsquo;s 3-year charge-off rate compares with the overall rate (1.00 = same), for FY
            {m.outcome.calibratedOnFiscalYears.join('–')} loans. This compares industries only; it is not a fair-lending
            measure, which would need applicant data the public dataset does not contain. Groups under 50 loans are left out.
          </p>
          <div className="table-scroll">
            <table className="table">
              <thead><tr><th>Industry</th><th className="r">Ratio to overall</th><th className="r">Loans</th></tr></thead>
              <tbody>{MODEL.industryParity.map((r) => <tr key={r.naics2}><td>{r.label}</td><td className="r">{r.ratio.toFixed(2)}</td><td className="r">{r.n.toLocaleString('en-US')}</td></tr>)}</tbody>
            </table>
          </div>
        </section>

        <section className="card flat">
          <h2>Sources</h2>
          <ul className="small">
            <li><a href={m.citation} target="_blank" rel="noreferrer">SBA 7(a) &amp; 504 FOIA loan data</a> (as of {m.asOf})</li>
            <li><a href={params.variableRateSpreadTiers.citation} target="_blank" rel="noreferrer">{params.variableRateSpreadTiers.source}</a></li>
            <li><a href={params.baseRate.citation} target="_blank" rel="noreferrer">{params.baseRate.source}</a></li>
          </ul>
        </section>
      </div>
    </>
  );
}
